import HotelIcon from '@mui/icons-material/Hotel';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import PlaceIcon from '@mui/icons-material/Place';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Portal,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useCallback, useEffect, useLayoutEffect, useRef, useState, Fragment } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useNavigate } from 'react-router-dom';
import { useSearchContext } from '../../context/SearchContext';

import searchNormalIcon from '../../assets/search-normal.svg';
/** Same geometry as the Flight swap arrow, drawn with a heavier stroke for the hotel compact card. */
import hotel_swap_icon from '../../assets/hotel_swap_icon.svg';
import { API_BASE_URL } from '../../config/backendConfig';
import {
  createGtmAttributes,
  GTM_CLICK_IDS,
  GTM_CONTEXT_ATTRS,
} from '../../analytics/gtmClickIds';
import { trackGtmEvent } from '../../analytics/gtm';
import { pickLocationId, appendHotelGeoSearchParams, hasValidSearchCoordinates } from '../../utils/hotelSearchParams';
import {
  isHotelAutocompleteQueryReady,
  getHotelAutocompleteRequestQuery,
} from '../../utils/hotelAutocompleteQuery';
import {
  partitionHotelAutocompleteResults,
  toHotelLocationSelectInput,
  getHotelAutocompleteSectionOrder,
} from '../../utils/hotelAutocompleteResults';
import { resolveSearchIntent, getErrorMessage } from '../../utils/hotelSearchIntentResolver';
import HotelFuzzyMatchName from '../Hotels/HotelFuzzyMatchName/HotelFuzzyMatchName';
import HotelAutocompleteLabel from '../Hotels/HotelAutocompleteLabel/HotelAutocompleteLabel';
import HotelAutocompleteStars from '../Hotels/HotelAutocompleteStars/HotelAutocompleteStars';
import HotelAutocompleteLoadMore from '../Hotels/HotelAutocompleteLoadMore/HotelAutocompleteLoadMore';
import HotelLocationTypeIcon from '../Hotels/HotelLocationTypeIcon/HotelLocationTypeIcon';
import { sliceAutocompleteSection } from '../../utils/hotelAutocompleteSection';
import { promptBrowserLocationPermission } from '../../utils/geolocation';
import { shouldCloseHotelDestinationDropdown } from './hotelDestinationClose';
import {
  HOTEL_AUTOCOMPLETE_EMPTY_FALLBACK_QUERY,
  HOTEL_SEARCH_ICON_COLOR,
  HOTEL_SEARCH_INTENT_ACTION,
} from '../../constants/hotelSearch.constants';
import './styles/HotelForm.css';
/** Shared mobile date / traveller / flight-dropdown surface used by MobileSearchDateSheet & destination search bar */
import MobileSearchDateSheet from './MobileSearchDateSheet';
import HotelMobileDestinationSheet from './hotelForm/HotelMobileDestinationSheet';
import HotelMobileRoomsGuestsSheet from './hotelForm/HotelMobileRoomsGuestsSheet';
import HotelNearbyLocationOption from '../Hotels/HotelNearbyLocationOption/HotelNearbyLocationOption';
import { MOBILE_HOMEPAGE_LAYOUT as L } from './mobileHomepageLayout';
import { getHotelSearchCardChrome } from './hotelSearchCardChrome';
import {
  HOTEL_SEARCH_STRIP_ROW_GAP,
  getHotelSearchDateColumnLayout,
  getHotelSearchStripPadding,
  getHotelSearchDateValueSx,
  getHotelSearchDestinationLayout,
  getHotelSearchPackedFieldLayout,
  getHotelSearchStripBandLayout,
  getHotelSearchWrappedTailLayout,
} from './hotelSearchDateField';
import './styles/FlightForm.css';

/** Compact footer copy for hotel guest sheet (matches Flight travellers sheet date line). */
function formatShortTripDate(d) {
  if (!d) return '';
  const day = d.getDate();
  const k = day % 100;
  const suf =
    k > 10 && k < 14
      ? 'th'
      : day % 10 === 1
        ? 'st'
        : day % 10 === 2
          ? 'nd'
          : day % 10 === 3
            ? 'rd'
            : 'th';
  const mon = d.toLocaleDateString('en-US', { month: 'short' });
  const yy = String(d.getFullYear()).slice(-2);
  return `${day}${suf} ${mon}'${yy}`;
}

const HotelForm = ({ onStateChange, onAuthRequired, variant, initialDestination, initialSearch }) => {
  const isEmbedded = variant === 'embedded';
  const theme = useTheme();
  // MUI md = 900px; mobile + tablet = below md (0–899px)
  const isCompact = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
  /** SEO hero desktop: portal dropdowns to document.body so hero overflow/stacking does not break them. */
  const useEmbeddedPortals = isEmbedded && !isCompact;

  // State variables - search results (keep separate for smart ordering)
  const [cityResults, setCityResults] = useState([]);
  const [hotelResults, setHotelResults] = useState([]);
  const [locationResults, setLocationResults] = useState([]);
  const [expandedAutocomplete, setExpandedAutocomplete] = useState({
    city: false,
    hotel: false,
    location: false,
  });
  const [queryType, setQueryType] = useState('city'); // 'city' | 'hotel' for section ordering
  const [locationInput, setLocationInput] = useState(
    () => initialDestination?.displayName || initialDestination?.cityName || '',
  );
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);

  // Selection tracking state
  const [selectedType, setSelectedType] = useState(() =>
    initialDestination?.cityName ? 'city' : null,
  );
  const [selectedHotelCode, setSelectedHotelCode] = useState('');
  const [selectedHotelName, setSelectedHotelName] = useState('');
  const [selectedCityId, setSelectedCityId] = useState(() => initialDestination?.cityId || '');
  const [selectedCityName, setSelectedCityName] = useState(() => initialDestination?.cityName || initialDestination?.displayName || '');
  const [selectedCountryCode, setSelectedCountryCode] = useState(
    () => initialDestination?.countryCode || '',
  );
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [selectedLocationType, setSelectedLocationType] = useState('');
  const [selectedLocationName, setSelectedLocationName] = useState('');
  const [selectedLatitude, setSelectedLatitude] = useState(null);
  const [selectedLongitude, setSelectedLongitude] = useState(null);
  const [validationError, setValidationError] = useState('');
  const [isResolving, setIsResolving] = useState(false);

  // Legacy location state for compatibility
  const [location, setLocation] = useState(null);
  const [locationOptions, setLocationOptions] = useState([]);
  const parseInitialYmd = (ymd) => {
    if (!ymd) return null;
    const d = new Date(`${ymd}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const [checkInDate, setCheckInDate] = useState(() => parseInitialYmd(initialSearch?.checkIn));
  const [checkOutDate, setCheckOutDate] = useState(() => parseInitialYmd(initialSearch?.checkOut));
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [datePickerTrigger, setDatePickerTrigger] = useState(null); // 'checkin' or 'checkout'
  const [dateRange, setDateRange] = useState([null, null]);
  const [firstSelectedDate, setFirstSelectedDate] = useState(null); // Track first selection when checkout clicked first
  const [rooms, setRooms] = useState([{ adults: 1, children: 0, childrenAges: [] }]);
  const [showGuestDropdown, setShowGuestDropdown] = useState(false);
  const [guestNationality, setGuestNationality] = useState(() => initialSearch?.nationality || 'IN');
  const [showNationalityDropdown, setShowNationalityDropdown] = useState(false);
  const [showNationalityBlock, setShowNationalityBlock] = useState(false);

  /** Mobile-only: Flight-style destination sheet + Search Date drafts */
  const [mobileHotelDestOpen, setMobileHotelDestOpen] = useState(false);
  const [mobileHotelDraftStart, setMobileHotelDraftStart] = useState(null);
  const [mobileHotelDraftEnd, setMobileHotelDraftEnd] = useState(null);
  const [hotelMobileDateSegment, setHotelMobileDateSegment] = useState('checkin');

  const NATIONALITY_OPTIONS = [
    { code: 'IN', label: 'Indian' }, { code: 'US', label: 'American' }, { code: 'GB', label: 'British' },
    { code: 'CA', label: 'Canadian' }, { code: 'AU', label: 'Australian' }, { code: 'DE', label: 'German' },
    { code: 'FR', label: 'French' }, { code: 'JP', label: 'Japanese' }, { code: 'AE', label: 'UAE' },
    { code: 'SG', label: 'Singaporean' }, { code: 'NZ', label: 'New Zealand' }, { code: 'IT', label: 'Italian' },
    { code: 'ES', label: 'Spanish' }, { code: 'NL', label: 'Dutch' }, { code: 'CH', label: 'Swiss' },
    { code: 'SE', label: 'Swedish' }, { code: 'KR', label: 'South Korean' }, { code: 'TH', label: 'Thai' },
    { code: 'MY', label: 'Malaysian' }, { code: 'BD', label: 'Bangladeshi' }, { code: 'NP', label: 'Nepalese' },
    { code: 'LK', label: 'Sri Lankan' }, { code: 'ZA', label: 'South African' },
  ];
  const nationalityLabel = NATIONALITY_OPTIONS.find(n => n.code === guestNationality)?.label || guestNationality;
  const searchRequestRef = useRef(0);
  const locationInputRef = useRef(null);
  /** Compact nationality menu is portalled out of the homepage card's stacking context; anchor to the field. */
  const nationalityAnchorRef = useRef(null);
  const desktopNationalityAnchorRef = useRef(null);
  const destinationAnchorRef = useRef(null);
  const checkInDateAnchorRef = useRef(null);
  const checkOutDateAnchorRef = useRef(null);
  const guestAnchorRef = useRef(null);
  const [nationalityMenuPos, setNationalityMenuPos] = useState(null);
  const [embeddedLocationPos, setEmbeddedLocationPos] = useState(null);
  const [embeddedDatePickerPos, setEmbeddedDatePickerPos] = useState(null);
  const [embeddedGuestPos, setEmbeddedGuestPos] = useState(null);
  /** Ref to the in-dropdown search input (desktop). Focused when the dropdown opens
   *  so typing flows naturally — mirrors Flight FROM/TO popper behaviour. */
  const desktopDropdownSearchRef = useRef(null);
  const pendingPlaceDataRef = useRef(null); // Store place data to auto-select after search
  const isAutoFillingRef = useRef(false); // Flag to prevent debounced search during auto-fill
  /** Suppresses exactly one cycle of the debounced search. Set right before
   *  handleLocationSelect updates `locationInput` so the resulting input-change
   *  effect doesn't fire a fresh API call that would re-open the dropdown after
   *  the user has already picked a destination. */
  const skipNextSearchRef = useRef(false);
  /** True only for the click that opens the destination list, so that click cannot close it. */
  const suppressDestinationCloseRef = useRef(false);
  const armDestinationCloseGuard = () => {
    suppressDestinationCloseRef.current = true;
    window.setTimeout(() => {
      suppressDestinationCloseRef.current = false;
    }, 0);
  };

  const navigate = useNavigate();
  const { updateSearchParams } = useSearchContext();

  // Close date pickers and dropdowns when clicking outside.
  // The listener is attached after the opening gesture so that click cannot dismiss the list.
  useEffect(() => {
    let detach = () => {};
    const timeoutId = window.setTimeout(() => {
      const handleClickOutside = (event) => {
        const target = event.target;
        if (showDatePicker && !target.closest('[data-date-picker]')) {
          setShowDatePicker(false);
          setDatePickerTrigger(null);
          setDateRange([null, null]);
        }
        if (showGuestDropdown && !target.closest('[data-guest-dropdown]') && !target.closest('[data-hotel-rooms-guests-sheet]')) {
          setShowGuestDropdown(false);
        }
        if (showNationalityDropdown && !target.closest('[data-nationality-dropdown]')) {
          setShowNationalityDropdown(false);
        }
        const insideLocationField = Boolean(
          (locationInputRef.current && locationInputRef.current.contains(target))
          || (destinationAnchorRef.current && destinationAnchorRef.current.contains(target)),
        );
        const { closeLocation, closeMobile } = shouldCloseHotelDestinationDropdown({
          suppressClose: suppressDestinationCloseRef.current,
          locationDropdownOpen,
          mobileHotelDestOpen,
          insideLocationDropdown: Boolean(target.closest?.('[data-location-dropdown]')),
          insideLocationField,
          insideDestinationSheet: Boolean(target.closest?.('[data-hotel-destination-sheet]')),
          insideDestinationTrigger: Boolean(target.closest?.('[data-hotel-destination-trigger]')),
        });
        if (closeLocation) {
          setLocationDropdownOpen(false);
        }
        if (closeMobile) {
          setMobileHotelDestOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      detach = () => document.removeEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
      detach();
    };
  }, [showDatePicker, showGuestDropdown, showNationalityDropdown, locationDropdownOpen, mobileHotelDestOpen]);

  useEffect(() => {
    setExpandedAutocomplete({ city: false, hotel: false, location: false });
  }, [cityResults, hotelResults, locationResults]);

  /* Keep portalled nationality menus pinned under their field while open (compact + SEO hero desktop). */
  useEffect(() => {
    const usePortalledNationality = isCompact || useEmbeddedPortals;
    if (!usePortalledNationality || !showNationalityDropdown) {
      setNationalityMenuPos(null);
      return undefined;
    }

    const syncPosition = () => {
      const anchor = isCompact ? nationalityAnchorRef.current : desktopNationalityAnchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      setNationalityMenuPos({ top: rect.bottom + 8, left: rect.left, width: rect.width });
    };

    syncPosition();
    window.addEventListener('scroll', syncPosition, true);
    window.addEventListener('resize', syncPosition);
    return () => {
      window.removeEventListener('scroll', syncPosition, true);
      window.removeEventListener('resize', syncPosition);
    };
  }, [isCompact, useEmbeddedPortals, showNationalityDropdown]);

  /** SEO hero desktop: sync anchor rect before open so the portaled menu never renders in-flow. */
  const openLocationDropdown = useCallback(() => {
    if (useEmbeddedPortals) {
      const anchor = destinationAnchorRef.current;
      if (anchor) {
        const rect = anchor.getBoundingClientRect();
        setEmbeddedLocationPos({ top: rect.bottom + 8, left: rect.left });
      }
    }
    setLocationDropdownOpen(true);
    if (!isEmbedded) {
      promptBrowserLocationPermission();
    }
  }, [useEmbeddedPortals, isEmbedded]);

  useLayoutEffect(() => {
    if (!useEmbeddedPortals || !locationDropdownOpen) {
      setEmbeddedLocationPos(null);
      return undefined;
    }
    const sync = () => {
      const anchor = destinationAnchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      setEmbeddedLocationPos({ top: rect.bottom + 8, left: rect.left });
    };
    sync();
    window.addEventListener('scroll', sync, true);
    window.addEventListener('resize', sync);
    return () => {
      window.removeEventListener('scroll', sync, true);
      window.removeEventListener('resize', sync);
    };
  }, [useEmbeddedPortals, locationDropdownOpen]);

  useLayoutEffect(() => {
    if (!useEmbeddedPortals || !showDatePicker) {
      setEmbeddedDatePickerPos(null);
      return undefined;
    }
    const sync = () => {
      const anchor = datePickerTrigger === 'checkout'
        ? checkOutDateAnchorRef.current
        : checkInDateAnchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const pickerWidth = Math.min(650, window.innerWidth - 16);
      const idealLeft = rect.left + rect.width / 2 - pickerWidth / 2;
      const left = Math.max(8, Math.min(idealLeft, window.innerWidth - pickerWidth - 8));
      setEmbeddedDatePickerPos({ top: rect.bottom + 8, left, width: pickerWidth });
    };
    sync();
    window.addEventListener('scroll', sync, true);
    window.addEventListener('resize', sync);
    return () => {
      window.removeEventListener('scroll', sync, true);
      window.removeEventListener('resize', sync);
    };
  }, [useEmbeddedPortals, showDatePicker, datePickerTrigger]);

  useLayoutEffect(() => {
    if (!useEmbeddedPortals || !showGuestDropdown) {
      setEmbeddedGuestPos(null);
      return undefined;
    }
    const sync = () => {
      const anchor = guestAnchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const menuWidth = 340;
      const left = Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8));
      setEmbeddedGuestPos({ top: rect.bottom + 8, left, width: menuWidth });
    };
    sync();
    window.addEventListener('scroll', sync, true);
    window.addEventListener('resize', sync);
    return () => {
      window.removeEventListener('scroll', sync, true);
      window.removeEventListener('resize', sync);
    };
  }, [useEmbeddedPortals, showGuestDropdown]);

  // Handle location search from TBO static cities - uses normalized API response
  const handleLocationSearch = useCallback(async (input) => {
    const searchQuery = getHotelAutocompleteRequestQuery(input, {
      fallbackQuery: HOTEL_AUTOCOMPLETE_EMPTY_FALLBACK_QUERY,
    });
    if (searchQuery == null) {
      searchRequestRef.current += 1;
      setCityResults([]);
      setHotelResults([]);
      setLocationResults([]);
      setLocationOptions([]);
      setLocationLoading(false);
      return;
    }
    const isDefaultSearch = !isHotelAutocompleteQueryReady(input);

    const currentRequest = ++searchRequestRef.current;
    
    setLocationLoading(true);
    try {
       const response = await fetch(`${API_BASE_URL}/hotels/tbo/search-autocomplete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: searchQuery }),
      });
      const result = await response.json();
      
      if (currentRequest === searchRequestRef.current) {
        if (result.success) {
          const partitioned = partitionHotelAutocompleteResults(result, {
            includeHotels: !isDefaultSearch,
          });
          const cities = partitioned.cities;
          const hotels = partitioned.hotels;
          const locations = partitioned.locations;
          
          // Store results separately for smart ordering
          setCityResults(cities);
          setHotelResults(hotels);
          setLocationResults(locations);
          setQueryType(partitioned.queryType || 'city');
          
          // Legacy: also set flattened locationOptions for backward compatibility
          const legacyCities = cities.map(c => ({
            type: 'city',
            id: c.cityId,
            cityId: c.cityId,
            name: c.label,
            city: c.cityName,
            country: c.countryName,
            countryCode: c.countryCode,
            hotelCount: c.hotelCount,
          }));
          const legacyHotels = hotels.map(h => ({
            type: 'hotel',
            id: h.hotelCode,
            hotelCode: h.hotelCode,
            name: h.hotelName,
            city: h.cityName,
            cityId: h.cityId,
            country: h.countryName,
            countryCode: h.countryCode,
            starRating: h.starRating,
          }));
          setLocationOptions([...legacyCities, ...legacyHotels]);
        } else {
          setCityResults([]);
          setHotelResults([]);
          setLocationResults([]);
          setLocationOptions([]);
          pendingPlaceDataRef.current = null;
        }
      }
    } catch (error) {
      if (currentRequest === searchRequestRef.current) {
        console.error('Location search error:', error);
        setCityResults([]);
        setHotelResults([]);
        setLocationResults([]);
        setLocationOptions([]);
      }
    } finally {
      if (currentRequest === searchRequestRef.current) {
        setLocationLoading(false);
      }
    }
  }, []);

  // Handle location selection - stores both hotel and city context
  const handleLocationSelect = useCallback((selectedLocation) => {
    /* The select-flow updates `locationInput` (see setters below). Without these two
     *  guards the input-change effect would fire a fresh debounced search 300ms later
     *  and re-open the dropdown the user just dismissed. */
    skipNextSearchRef.current = true;
    /* Invalidate any in-flight search request so its eventual response can no longer
     *  call `setLocationDropdownOpen(true)` (the request-counter check inside
     *  handleLocationSearch will fail for the now-stale id). */
    searchRequestRef.current += 1;

    setLocation(selectedLocation);
    setValidationError('');

    if (hasValidSearchCoordinates(selectedLocation.latitude, selectedLocation.longitude)) {
      setSelectedLatitude(selectedLocation.latitude);
      setSelectedLongitude(selectedLocation.longitude);
    } else {
      setSelectedLatitude(null);
      setSelectedLongitude(null);
    }
    
    if (selectedLocation.type === 'hotel') {
      // Hotel selection - store BOTH hotel AND city context
      setSelectedType('hotel');
      setSelectedHotelCode(selectedLocation.hotelCode || selectedLocation.id);
      setSelectedHotelName(selectedLocation.name || selectedLocation.hotelName);
      setSelectedCityId(selectedLocation.cityId || '');
      setSelectedCityName(selectedLocation.city || selectedLocation.cityName || '');
      setSelectedCountryCode(selectedLocation.countryCode || '');
      setLocationInput(selectedLocation.name || selectedLocation.hotelName);
      setSelectedLocationId('');
      setSelectedLocationType('');
      setSelectedLocationName('');
    } else if (selectedLocation.type === 'location') {
      // Location selection (airport, landmark, area, etc.)
      setSelectedType('location');
      setSelectedHotelCode('');
      setSelectedHotelName('');
      setSelectedCityId(selectedLocation.cityId || '');
      setSelectedCityName(selectedLocation.cityName || '');
      setSelectedCountryCode(selectedLocation.countryCode || '');
      setLocationInput(selectedLocation.displayName || selectedLocation.name || '');
      setSelectedLocationId(pickLocationId(selectedLocation));
      setSelectedLocationType(selectedLocation.locationType || 'LOCATION');
      setSelectedLocationName(selectedLocation.displayName || '');
    } else {
      // City selection - clear hotel context
      setSelectedType('city');
      setSelectedHotelCode('');
      setSelectedHotelName('');
      setSelectedCityId(selectedLocation.cityId || selectedLocation.id);
      setSelectedCityName(selectedLocation.city || selectedLocation.cityName);
      setSelectedCountryCode(selectedLocation.countryCode || '');
      setLocationInput(selectedLocation.city || selectedLocation.cityName);
      setSelectedLocationId('');
      setSelectedLocationType('');
      setSelectedLocationName('');
    }
    
    setLocationDropdownOpen(false);
    setMobileHotelDestOpen(false);

    // Auto-open check-in date picker
    if (!checkInDate) {
      setTimeout(() => {
        setDatePickerTrigger('checkin');
        setDateRange([null, checkOutDate]);
        if (isCompact) {
          setMobileHotelDraftStart(checkInDate);
          setMobileHotelDraftEnd(checkOutDate);
          setHotelMobileDateSegment('checkin');
        }
        setShowDatePicker(true);
      }, 100);
    }
  }, [checkInDate, checkOutDate, isCompact]);

  // Handle input change - clears stale selection when user edits
  const handleLocationInputChange = useCallback((value) => {
    setLocationInput(value);
    setValidationError('');
    
    // Clear ALL stale selection values when user edits input
    if (selectedType) {
      setSelectedType(null);
      setSelectedHotelCode('');
      setSelectedHotelName('');
      setSelectedCityId('');
      setSelectedCityName('');
      setSelectedCountryCode('');
      setSelectedLocationId('');
      setSelectedLocationType('');
      setSelectedLocationName('');
      setSelectedLatitude(null);
      setSelectedLongitude(null);
      setLocation(null);
    }
    
    // Search is triggered by useEffect debounce, not here (avoids duplicate requests)
  }, [selectedType]);

  // Check for stored place data from IstanbulGuide on mount
  useEffect(() => {
    const storedPlaceData = localStorage.getItem('yovo_place_data');
    if (storedPlaceData && !location) {
      try {
        const placeData = JSON.parse(storedPlaceData);
        if (placeData.city && placeData.country) {
          // Set auto-fill flag to prevent debounced search interference
          isAutoFillingRef.current = true;
          
          // Pre-fill the location input
          const locationText = `${placeData.city}, ${placeData.country}`;
          setLocationInput(locationText);
          
          // Store place data to auto-select after search results
          pendingPlaceDataRef.current = placeData;
          
          // Open dropdown to show results
          openLocationDropdown();
          if (typeof window !== 'undefined' && window.matchMedia('(max-width: 899.98px)').matches) {
            setMobileHotelDestOpen(true);
          }

          // Trigger location search to get full location object
          // Use a small delay to ensure the input is set first
          setTimeout(() => {
            handleLocationSearch(placeData.city);
          }, 100);
          
          // Clear the stored data after use
          localStorage.removeItem('yovo_place_data');
        }
      } catch (error) {
        console.error('Error parsing stored place data:', error);
        localStorage.removeItem('yovo_place_data');
        pendingPlaceDataRef.current = null;
        isAutoFillingRef.current = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Auto-select location when options are loaded and we have pending place data
  useEffect(() => {
    if (pendingPlaceDataRef.current && locationOptions.length > 0 && !location && !locationLoading) {
      const placeData = pendingPlaceDataRef.current;
      // Try to find exact match first
      let selectedLocation = locationOptions.find(
        loc => loc.city.toLowerCase() === placeData.city.toLowerCase() &&
               loc.country.toLowerCase() === placeData.country.toLowerCase()
      );
      
      // If no exact match, use first result
      if (!selectedLocation) {
        selectedLocation = locationOptions[0];
      }
      
      // Auto-select the location
      if (selectedLocation) {
        handleLocationSelect(selectedLocation);
        // Clear auto-fill flag after selection
        isAutoFillingRef.current = false;
      }
      
      // Clear pending place data
      pendingPlaceDataRef.current = null;
    }
  }, [locationOptions, location, locationLoading, handleLocationSelect]); // Run when locationOptions change or location is set

  /** Desktop: when the destination dropdown opens, focus the in-list search input
   *  so the user can start typing immediately (matches Flight FROM/TO behaviour). */
  useEffect(() => {
    if (!locationDropdownOpen || isCompact) return;
    const id = window.setTimeout(() => {
      desktopDropdownSearchRef.current?.focus({ preventScroll: true });
    }, 0);
    return () => window.clearTimeout(id);
  }, [locationDropdownOpen, isCompact]);

  // Handle location input change with debouncing
  useEffect(() => {
    // Skip debounced search if we're auto-filling from IstanbulGuide
    if (isAutoFillingRef.current) {
      return;
    }

    /* Skip the one cycle that fires immediately after handleLocationSelect — the
     *  user just chose a destination, so a follow-up API call would only serve to
     *  re-open the dropdown they just dismissed. */
    if (skipNextSearchRef.current) {
      skipNextSearchRef.current = false;
      return;
    }

    // Search when user types 2+ non-space characters — empty default search runs only via explicit open/click handlers.
    if (isHotelAutocompleteQueryReady(locationInput)) {
      const timeoutId = setTimeout(() => {
        handleLocationSearch(locationInput);
      }, 300);

      return () => {
        clearTimeout(timeoutId);
      };
    }

    if (locationInput.length >= 1) {
      setLocationOptions([]);
      setCityResults([]);
      setHotelResults([]);
      setLocationResults([]);
    }
  }, [locationInput, handleLocationSearch]);

  // Format date for display
  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Format date for API (YYYY-MM-DD)
  const formatDateForAPI = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Format date for mobile homepage hotel card — matches Figma 3013:152666 (e.g. 12 Jan'26)
  const formatDateMobileFigma = (date) => {
    if (!date) return '';
    const day = date.getDate();
    const mon = date.toLocaleDateString('en-US', { month: 'short' });
    const yy = String(date.getFullYear()).slice(-2);
    return `${day} ${mon}'${yy}`;
  };

  const encodeRoomsParam = (roomList) => roomList.map((r) => {
    const parts = [r.adults, r.children];
    if (r.children > 0) parts.push(...r.childrenAges);
    return parts.join('-');
  }).join('_');

  const proceedWithResolvedSelection = (type, match) => {
    const formattedCheckIn = formatDateForAPI(checkInDate);
    const formattedCheckOut = formatDateForAPI(checkOutDate);
    const matchCityId = match.cityId;
    const matchCityName = match.cityName;
    const matchCountryCode = match.countryCode;
    const isLocation = type === 'location' || match.locationId || match.locationType;

    const isInternational = matchCountryCode && matchCountryCode !== 'IN';
    if (guestNationality !== 'IN' && isInternational) {
      handleLocationSelect(toHotelLocationSelectInput({ ...match, type }));
      setShowNationalityBlock(true);
      return;
    }

    updateSearchParams({
      cityId: matchCityId,
      cityName: matchCityName,
      countryCode: matchCountryCode,
      checkInDate: formattedCheckIn,
      checkOutDate: formattedCheckOut,
      rooms,
      nationality: guestNationality,
      searchType: 'hotel',
    });

    const params = new URLSearchParams({
      checkIn: formattedCheckIn,
      checkOut: formattedCheckOut,
      rooms: encodeRoomsParam(rooms),
      nationality: guestNationality,
    });

    if (matchCityId) {
      params.set('cityId', matchCityId.toString());
    }
    params.set('cityName', matchCityName || '');
    params.set('countryCode', matchCountryCode || '');

    if (type === 'hotel') {
      params.set('hotelCode', match.hotelCode || '');
      params.set('hotelName', match.hotelName || '');
    }

    const resolvedLocationId = pickLocationId(match);
    if (isLocation && resolvedLocationId) {
      params.set('locationId', resolvedLocationId);
      params.set('locationType', match.locationType || 'LOCATION');
      params.set('searchLocationName', match.displayName || '');
    }
    appendHotelGeoSearchParams(params, match);

    trackGtmEvent('hotel_search_initiated', {
      gtm_click_id: GTM_CLICK_IDS.HOTEL_SEARCH_LETS_GO,
      gtm_context: 'home',
    });

    navigate(`/hotel?${params.toString()}`);
  };

  const handleSearch = async () => {
    setValidationError('');

    if (locationInput && locationInput.length >= 2 && !selectedType) {
      setIsResolving(true);
      try {
        const resolution = await resolveSearchIntent(locationInput, API_BASE_URL);
        if (resolution.action === HOTEL_SEARCH_INTENT_ACTION.AUTO_SELECT) {
          if (!checkInDate || !checkOutDate) {
            handleLocationSelect(toHotelLocationSelectInput({
              ...resolution.data,
              type: resolution.type,
            }));
            return;
          }
          proceedWithResolvedSelection(resolution.type, resolution.data);
          return;
        }

        if (resolution.action === HOTEL_SEARCH_INTENT_ACTION.SHOW_DROPDOWN) {
          const partitioned = partitionHotelAutocompleteResults({
            success: true,
            ...resolution.results,
          });
          setCityResults(partitioned.cities);
          setHotelResults(partitioned.hotels);
          setLocationResults(partitioned.locations);
          setQueryType(partitioned.queryType);
          setValidationError(resolution.message || 'Please select from the list.');
          openLocationDropdown();
          if (isCompact) {
            setMobileHotelDestOpen(true);
            setTimeout(() => document.querySelector('[data-hotel-destination-input]')?.focus?.(), 280);
          }
          return;
        }

        setValidationError(getErrorMessage(resolution));
        openLocationDropdown();
        if (isCompact) {
          setMobileHotelDestOpen(true);
          setTimeout(() => document.querySelector('[data-hotel-destination-input]')?.focus?.(), 280);
        }
        return;
      } catch (err) {
        console.warn('Intent resolution failed:', err);
        setValidationError('Search failed. Please try again.');
        return;
      } finally {
        setIsResolving(false);
      }
    }

    // Validation: No destination selected at all
    if (!selectedType && !location) {
      setValidationError('Please select a city or hotel from the list.');
      openLocationDropdown();
      if (!locationInput || locationInput.length < 2) {
        handleLocationSearch('');
      }
      if (isCompact) {
        setMobileHotelDestOpen(true);
        setTimeout(() => document.querySelector('[data-hotel-destination-input]')?.focus?.(), 280);
      } else {
        setTimeout(() => {
          if (locationInputRef.current) {
            const inputElement = locationInputRef.current.querySelector('input');
            if (inputElement) inputElement.focus({ preventScroll: true });
          }
        }, 100);
      }
      return;
    }

    // Validate check-in date and auto-open picker
    if (!checkInDate) {
      if (isCompact) {
        setMobileHotelDraftStart(checkInDate);
        setMobileHotelDraftEnd(checkOutDate);
        setHotelMobileDateSegment('checkin');
      }
      setDatePickerTrigger('checkin');
      setDateRange([null, checkOutDate]);
      setShowDatePicker(true);
      return;
    }

    // Validate check-out date and auto-open picker
    if (!checkOutDate) {
      if (isCompact) {
        setMobileHotelDraftStart(checkInDate);
        setMobileHotelDraftEnd(checkOutDate);
        setHotelMobileDateSegment('checkout');
      }
      setDatePickerTrigger('checkout');
      setDateRange([checkInDate, null]);
      setShowDatePicker(true);
      return;
    }

    // Validate check-out date is on or after check-in date
    if (checkOutDate < checkInDate) {
      alert('Check-out date must be on or after check-in date');
      if (isCompact) {
        setMobileHotelDraftStart(checkInDate);
        setMobileHotelDraftEnd(checkOutDate);
        setHotelMobileDateSegment('checkout');
      }
      setDatePickerTrigger('checkout');
      setDateRange([checkInDate, null]);
      setShowDatePicker(true);
      return;
    }

    // Use new selection state or fall back to legacy location object
    const cityId = selectedCityId || (location?.cityId || location?.id);
    const cityName = selectedCityName || location?.city;
    const countryCode = selectedCountryCode || location?.countryCode;
    
    const isInternational = countryCode && countryCode !== 'IN';
    if (guestNationality !== 'IN' && isInternational) {
      setShowNationalityBlock(true);
      return;
    }

    const formattedCheckIn = formatDateForAPI(checkInDate);
    const formattedCheckOut = formatDateForAPI(checkOutDate);

    updateSearchParams({
      cityId,
      cityName,
      countryCode,
      checkInDate: formattedCheckIn,
      checkOutDate: formattedCheckOut,
      rooms: rooms,
      nationality: guestNationality,
      searchType: 'hotel'
    });

    const params = new URLSearchParams({
      checkIn: formattedCheckIn,
      checkOut: formattedCheckOut,
      rooms: encodeRoomsParam(rooms),
      nationality: guestNationality,
    });

    // Always include city context (needed for fallback if hotel unavailable)
    if (cityId) {
      params.set('cityId', cityId.toString());
    }
    params.set('cityName', cityName || '');
    params.set('countryCode', countryCode || '');
    
    // If hotel selected, also include hotel-specific params
    if (selectedType === 'hotel' && selectedHotelCode) {
      params.set('hotelCode', selectedHotelCode);
      params.set('hotelName', selectedHotelName);
    }

    // If location selected (airport, landmark, etc.), include location params
    if (selectedLocationId) {
      params.set('locationId', selectedLocationId.toString());
      params.set('locationType', selectedLocationType || 'LOCATION');
      params.set('searchLocationName', selectedLocationName || '');
    }
    appendHotelGeoSearchParams(params, {
      latitude: selectedLatitude,
      longitude: selectedLongitude,
    });

    trackGtmEvent('hotel_search_initiated', {
      gtm_click_id: GTM_CLICK_IDS.HOTEL_SEARCH_LETS_GO,
      gtm_context: 'home',
    });

    navigate(`/hotel?${params.toString()}`);
  };

  const totalGuests = rooms.reduce((sum, r) => sum + r.adults + r.children, 0);
  const totalAdults = rooms.reduce((sum, r) => sum + r.adults, 0);
  const totalChildren = rooms.reduce((sum, r) => sum + r.children, 0);

  const handleSwapDates = useCallback(
    (e) => {
      e?.stopPropagation();
      if (checkInDate && checkOutDate) {
        setCheckInDate(checkOutDate);
        setCheckOutDate(checkInDate);
      }
    },
    [checkInDate, checkOutDate]
  );

  const handleHotelClearCheckin = useCallback(() => {
    setMobileHotelDraftStart(null);
    setHotelMobileDateSegment('checkin');
  }, []);

  const handleHotelClearCheckout = useCallback(() => {
    setMobileHotelDraftEnd(null);
    setHotelMobileDateSegment('checkout');
  }, []);

  const saveMobileHotelDates = useCallback(() => {
    if (!mobileHotelDraftStart || !mobileHotelDraftEnd) return;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(mobileHotelDraftStart);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(mobileHotelDraftEnd);
    endDate.setHours(0, 0, 0, 0);
    if (startDate < today) {
      alert('Check-in date cannot be in the past');
      return;
    }
    if (startDate > endDate) {
      alert('Check-in date must be on or before check-out date');
      return;
    }
    setCheckInDate(mobileHotelDraftStart);
    setCheckOutDate(mobileHotelDraftEnd);
    setShowDatePicker(false);
    setDatePickerTrigger(null);
    setFirstSelectedDate(null);
    setDateRange([null, null]);
  }, [mobileHotelDraftStart, mobileHotelDraftEnd]);

  const renderHotelMobileCalendars = () => (
    <Box className="mobile-date-fullscreen-datepicker-wrap">
      <DatePicker
        key={`hotel-mobile-cal-${mobileHotelDraftStart?.getTime?.() || 'x'}-${mobileHotelDraftEnd?.getTime?.() || 'y'}`}
        selected={mobileHotelDraftStart}
        onChange={(dates) => {
          if (Array.isArray(dates)) {
            const [st, en] = dates;
            setMobileHotelDraftStart(st ?? null);
            setMobileHotelDraftEnd(en ?? null);
          } else if (dates) {
            setMobileHotelDraftStart(dates);
            setMobileHotelDraftEnd(null);
          }
        }}
        startDate={mobileHotelDraftStart}
        endDate={mobileHotelDraftEnd}
        selectsRange
        dateFormat="MMM dd, yyyy"
        dateFormatCalendar="LLLL, yyyy"
        minDate={new Date()}
        inline
        showPopperArrow={false}
        calendarClassName="custom-datepicker-single-month mobile-fullscreen-datepicker"
        shouldCloseOnSelect={false}
        monthsShown={13}
        showPreviousMonths={false}
        openToDate={mobileHotelDraftStart || new Date()}
        formatWeekDay={(nameOfDay) => nameOfDay.substring(0, 3).toUpperCase()}
      />
    </Box>
  );

  const renderDatePicker = () => {
    if (!showDatePicker) return null;
    if (isCompact) return null;
    const popupStyle = useEmbeddedPortals && embeddedDatePickerPos
      ? {
          position: 'fixed',
          top: `${embeddedDatePickerPos.top}px`,
          left: `${embeddedDatePickerPos.left}px`,
          transform: 'none',
          minWidth: `${embeddedDatePickerPos.width}px`,
          maxWidth: `${embeddedDatePickerPos.width}px`,
          width: `${embeddedDatePickerPos.width}px`,
          zIndex: 1300,
          marginTop: 0,
        }
      : undefined;

    const popup = (
      <Box
        className={`hotel-date-picker-popup${useEmbeddedPortals && embeddedDatePickerPos ? ' hotel-dropdown-portaled' : ''}`}
        data-date-picker
        style={popupStyle}
      >
        <Box className="hotel-date-picker-inner">
          <DatePicker
            selectsRange
            startDate={dateRange[0]}
            endDate={dateRange[1]}
            onSelect={(date) => {
              if (datePickerTrigger === 'checkout' && firstSelectedDate && !checkInDate) {
                const checkoutDate = new Date(firstSelectedDate);
                checkoutDate.setHours(0, 0, 0, 0);
                const selectedDate = new Date(date);
                selectedDate.setHours(0, 0, 0, 0);
                if (selectedDate <= checkoutDate) {
                  setDateRange([selectedDate, checkoutDate]);
                }
              }
            }}
            onDayMouseEnter={(date) => {
              if (datePickerTrigger === 'checkout' && firstSelectedDate && !checkInDate) {
                const checkoutDate = new Date(firstSelectedDate);
                checkoutDate.setHours(0, 0, 0, 0);
                const hoveredDate = new Date(date);
                hoveredDate.setHours(0, 0, 0, 0);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                if (hoveredDate <= checkoutDate && hoveredDate >= today) {
                  setDateRange([hoveredDate, checkoutDate]);
                }
              }
            }}
            onChange={(dates) => {
              const [start, end] = dates;
              if (start && end) {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const startDate = new Date(start);
                startDate.setHours(0, 0, 0, 0);
                const endDate = new Date(end);
                endDate.setHours(0, 0, 0, 0);
                if (startDate < today) {
                  alert('Check-in date cannot be in the past');
                  if (datePickerTrigger === 'checkout' && firstSelectedDate) { setDateRange([null, firstSelectedDate]); } else { setDateRange([null, end]); }
                  return;
                }
                if (startDate > endDate) {
                  alert('Check-in date must be on or before check-out date');
                  if (datePickerTrigger === 'checkout' && firstSelectedDate) { setDateRange([null, firstSelectedDate]); } else { setDateRange([start, null]); }
                  return;
                }
                if (startDate.getTime() === endDate.getTime()) {
                  setCheckInDate(start);
                  setCheckOutDate(null);
                  setDateRange([start, null]);
                  setDatePickerTrigger('checkout');
                  return;
                }
                if (datePickerTrigger === 'checkout' && firstSelectedDate !== null) {
                  const checkoutDate = new Date(firstSelectedDate);
                  checkoutDate.setHours(0, 0, 0, 0);
                  if (startDate < today) {
                    alert('Check-in date cannot be in the past');
                    setDateRange([null, checkoutDate]); setFirstSelectedDate(null); setCheckInDate(null); setCheckOutDate(null);
                    return;
                  }
                  if (startDate >= checkoutDate) {
                    setDateRange([null, checkoutDate]); setFirstSelectedDate(null); setCheckInDate(null); setCheckOutDate(null);
                    return;
                  }
                  setCheckInDate(start);
                  setCheckOutDate(checkoutDate);
                  setDateRange([start, checkoutDate]);
                  setFirstSelectedDate(null);
                } else {
                  setCheckInDate(start);
                  setCheckOutDate(end);
                  setDateRange([start, end]);
                }
                setShowDatePicker(false);
                setDatePickerTrigger(null);
                setDateRange([null, null]);
              } else if (start && !end) {
                if (datePickerTrigger === 'checkin') {
                  if (checkOutDate && start >= checkOutDate) {
                    setCheckInDate(start); setCheckOutDate(null); setDateRange([start, null]);
                    setTimeout(() => { setDatePickerTrigger('checkout'); setDateRange([start, null]); }, 100);
                  } else if (checkOutDate) {
                    setCheckInDate(start);
                    setCheckOutDate(checkOutDate);
                    setDateRange([start, checkOutDate]);
                    setShowDatePicker(false); setDatePickerTrigger(null); setDateRange([null, null]);
                  } else {
                    setCheckInDate(start); setDateRange([start, null]);
                    setTimeout(() => { setDatePickerTrigger('checkout'); setDateRange([start, null]); }, 100);
                  }
                } else if (datePickerTrigger === 'checkout') {
                  if (checkInDate) {
                    const s = new Date(checkInDate); s.setHours(0,0,0,0);
                    const e = new Date(start); e.setHours(0,0,0,0);
                    if (e <= s) {
                      setDateRange([checkInDate, null]);
                      return;
                    }
                    setCheckOutDate(start);
                    setDateRange([checkInDate, start]);
                    setShowDatePicker(false); setDatePickerTrigger(null); setDateRange([null, null]);
                  } else if (firstSelectedDate !== null) {
                    const checkoutDate = new Date(firstSelectedDate);
                    checkoutDate.setHours(0, 0, 0, 0);
                    const checkInDateValue = new Date(start);
                    checkInDateValue.setHours(0, 0, 0, 0);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    if (checkInDateValue < today) { alert('Check-in date cannot be in the past'); setDateRange([null, checkoutDate]); return; }
                    if (checkInDateValue >= checkoutDate) { setDateRange([null, checkoutDate]); return; }
                    setCheckInDate(start); setCheckOutDate(checkoutDate); setDateRange([start, checkoutDate]);
                    setShowDatePicker(false); setDatePickerTrigger(null); setFirstSelectedDate(null); setDateRange([null, null]);
                  } else {
                    setFirstSelectedDate(start); setCheckOutDate(start); setDateRange([null, start]);
                  }
                }
              }
            }}
            dateFormat="MMM dd, yyyy"
            minDate={new Date()}
            maxDate={
              datePickerTrigger === 'checkout' && firstSelectedDate
                ? (() => { const d = new Date(firstSelectedDate); d.setHours(0,0,0,0); return d; })()
                : undefined
            }
            filterDate={(date) => {
              if (datePickerTrigger === 'checkout' && firstSelectedDate) {
                const checkoutDate = new Date(firstSelectedDate);
                checkoutDate.setHours(0, 0, 0, 0);
                const currentDate = new Date(date);
                currentDate.setHours(0, 0, 0, 0);
                return currentDate <= checkoutDate;
              }
              return true;
            }}
            inline
            showPopperArrow={false}
            calendarClassName="custom-datepicker-single-month"
            monthsShown={2}
            showPreviousMonths={false}
            openToDate={dateRange[0] || dateRange[1] || new Date()}
            shouldCloseOnSelect={false}
            formatWeekDay={(nameOfDay) => nameOfDay.substring(0, 3)}
          />
        </Box>
      </Box>
    );

    if (useEmbeddedPortals && embeddedDatePickerPos) {
      return <Portal>{popup}</Portal>;
    }

    return popup;
  };

  const updateRoom = (index, field, value) => {
    setRooms(prev => {
      const updated = [...prev];
      const room = { ...updated[index] };
      room[field] = value;
      if (field === 'children') {
        const ages = [...room.childrenAges];
        while (ages.length < value) ages.push(6);
        room.childrenAges = ages.slice(0, value);
      }
      updated[index] = room;
      return updated;
    });
  };

  const updateChildAge = (roomIndex, childIndex, age) => {
    setRooms(prev => {
      const updated = [...prev];
      const room = { ...updated[roomIndex] };
      const ages = [...room.childrenAges];
      ages[childIndex] = age;
      room.childrenAges = ages;
      updated[roomIndex] = room;
      return updated;
    });
  };

  const addRoom = () => {
    if (rooms.length < 6) {
      setRooms(prev => [...prev, { adults: 1, children: 0, childrenAges: [] }]);
    }
  };

  const removeRoom = (index) => {
    if (rooms.length > 1) {
      setRooms(prev => prev.filter((_, i) => i !== index));
    }
  };

  const renderCounter = (label, sublabel, value, min, max, onDecrease, onIncrease) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: '6px' }}>
      <Box>
        <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#20202A', fontFamily: 'Host Grotesk' }}>{label}</Typography>
        <Typography sx={{ fontSize: '11px', color: '#7E808F', fontFamily: 'Host Grotesk' }}>{sublabel}</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <IconButton className="hotel-guest-btn" size="small" onClick={onDecrease} disabled={value <= min}
          sx={{ border: '1.5px solid #E8E9ED', width: '32px', height: '32px', opacity: value <= min ? 0.3 : 1, '&:hover': { backgroundColor: '#f5f5f5' } }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 600, lineHeight: 1 }}>−</Typography>
        </IconButton>
        <Typography sx={{ fontSize: '16px', fontWeight: 600, minWidth: '24px', textAlign: 'center', fontFamily: 'Host Grotesk' }}>
          {value}
        </Typography>
        <IconButton className="hotel-guest-btn" size="small" onClick={onIncrease} disabled={value >= max}
          sx={{ border: '1.5px solid #E8E9ED', width: '32px', height: '32px', opacity: value >= max ? 0.3 : 1, '&:hover': { backgroundColor: '#f5f5f5' } }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 600, lineHeight: 1 }}>+</Typography>
        </IconButton>
      </Box>
    </Box>
  );

  const renderGuestDropdown = () => {
    if (!showGuestDropdown) return null;

    const totalAdults = rooms.reduce((s, r) => s + r.adults, 0);
    const totalChildren = rooms.reduce((s, r) => s + r.children, 0);

    if (isCompact) {
      const footerLine1 = `${rooms.length} Room${rooms.length === 1 ? '' : 's'}, ${totalAdults} Adult${totalAdults === 1 ? '' : 's'}, ${totalChildren} ${totalChildren === 1 ? 'Child' : 'Children'}`;
      const footerLine2 = checkInDate && checkOutDate ? `${formatShortTripDate(checkInDate)} – ${formatShortTripDate(checkOutDate)}` : '';
      return (
        <HotelMobileRoomsGuestsSheet
          open={showGuestDropdown}
          onClose={() => setShowGuestDropdown(false)}
          rooms={rooms}
          onUpdateRoom={updateRoom}
          onUpdateChildAge={updateChildAge}
          onAddRoom={addRoom}
          onRemoveRoom={removeRoom}
          footerLine1={footerLine1}
          footerLine2={footerLine2}
        />
      );
    }

    const summaryParts = [`${rooms.length} ${rooms.length === 1 ? 'Room' : 'Rooms'}`];
    summaryParts.push(`${totalAdults} ${totalAdults === 1 ? 'Adult' : 'Adults'}`);
    if (totalChildren > 0) summaryParts.push(`${totalChildren} ${totalChildren === 1 ? 'Child' : 'Children'}`);

    const guestDropdownStyle = useEmbeddedPortals && embeddedGuestPos
      ? {
          position: 'fixed',
          top: `${embeddedGuestPos.top}px`,
          left: `${embeddedGuestPos.left}px`,
          width: `${embeddedGuestPos.width}px`,
          zIndex: 1300,
          marginTop: 0,
        }
      : undefined;

    const content = (
      <Box
        className={`hotel-guest-dropdown${useEmbeddedPortals && embeddedGuestPos ? ' hotel-dropdown-portaled' : ''}`}
        data-guest-dropdown
        style={guestDropdownStyle}
        sx={{
          maxHeight: isCompact ? 'min(70dvh, 500px)' : '420px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >

        <Box sx={{ overflowY: 'auto', flex: 1, mr: '-4px', pr: '4px' }}>
          {rooms.map((room, rIdx) => (
            <Box key={rIdx} sx={{
              pb: '14px', mb: '14px',
              borderBottom: rIdx < rooms.length - 1 ? '1px solid #E8E9ED' : 'none',
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '8px' }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#20202A', fontFamily: 'Host Grotesk' }}>
                  Room {rIdx + 1}
                </Typography>
                {rooms.length > 1 && (
                  <Typography onClick={() => removeRoom(rIdx)}
                    sx={{ fontSize: '12px', fontWeight: 500, color: '#e53935', cursor: 'pointer', fontFamily: 'Host Grotesk', '&:hover': { textDecoration: 'underline' } }}>
                    Remove
                  </Typography>
                )}
              </Box>
              {renderCounter('Adults', 'Age 18+', room.adults, 1, 8,
                () => updateRoom(rIdx, 'adults', room.adults - 1),
                () => updateRoom(rIdx, 'adults', room.adults + 1)
              )}
              {renderCounter('Children', 'Age 0 - 17', room.children, 0, 4,
                () => updateRoom(rIdx, 'children', room.children - 1),
                () => updateRoom(rIdx, 'children', room.children + 1)
              )}
              {room.children > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', mt: '8px', pl: '2px' }}>
                  {room.childrenAges.map((age, cIdx) => (
                    <Box key={cIdx} sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Typography sx={{ fontSize: '12px', color: '#7E808F', fontFamily: 'Host Grotesk', whiteSpace: 'nowrap' }}>
                        Child {cIdx + 1}
                      </Typography>
                      <Box component="select" value={age} onChange={(e) => updateChildAge(rIdx, cIdx, parseInt(e.target.value))}
                        sx={{
                          width: '56px', padding: '4px 6px', borderRadius: '8px',
                          border: '1.5px solid #E8E9ED', fontSize: '13px', fontWeight: 500,
                          fontFamily: 'Host Grotesk', color: '#20202A', backgroundColor: '#fff',
                          cursor: 'pointer', outline: 'none', appearance: 'none', WebkitAppearance: 'none',
                          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' fill='none'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%237E808F' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E")`,
                          backgroundRepeat: 'no-repeat', backgroundPosition: 'right 6px center', pr: '20px',
                          '&:focus': { borderColor: '#20202A' },
                        }}>
                        {Array.from({ length: 18 }, (_, i) => (
                          <option key={i} value={i}>{i} yr</option>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          ))}

          {rooms.length < 6 && (
            <Box onClick={addRoom}
              sx={{
                width: '100%', py: '10px', mt: '2px',
                border: '1.5px dashed #E8E9ED', borderRadius: '10px',
                textAlign: 'center', cursor: 'pointer',
                transition: 'all 0.15s',
                '&:hover': { borderColor: '#20202A', '& .add-text': { color: '#20202A' } },
              }}>
              <Typography className="add-text" sx={{ fontSize: '13px', fontWeight: 600, color: '#7E808F', fontFamily: 'Host Grotesk', transition: 'color 0.15s' }}>
                + Add Room
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ pt: '12px', mt: '8px', borderTop: '1px solid #E8E9ED' }}>
          <Typography sx={{ fontSize: '12px', color: '#7E808F', textAlign: 'center', fontFamily: 'Host Grotesk', mb: '10px' }}>
            {summaryParts.join(' \u00B7 ')}
          </Typography>
          <Button fullWidth onClick={() => setShowGuestDropdown(false)}
            sx={{ bgcolor: '#20202A', color: '#fff', borderRadius: '10px', textTransform: 'none', fontWeight: 600, fontFamily: 'Host Grotesk', height: '42px', fontSize: '14px', '&:hover': { bgcolor: '#383A47' } }}>
            Done
          </Button>
        </Box>
      </Box>
    );

    if (useEmbeddedPortals && embeddedGuestPos) {
      return <Portal>{content}</Portal>;
    }

    return content;
  };

  return (
    <Box sx={{
      position: 'relative',
      zIndex: { xs: 'auto', sm: 'auto', md: 'auto' },
      mt: isEmbedded
        ? 0
        : { xs: '0px', sm: '0px', md: '-80px', lg: '-90px', xl: '-90px' },
      width: isEmbedded
        ? '100%'
        : {
          xs: '100%',
          sm: 'calc(100% - 48px)',
          md: 'calc(100% - 80px)',
          lg: 'calc(100% - 96px)',
          xl: 'calc(100% - 96px)',
        },
      maxWidth: isEmbedded
        ? '100%'
        : {
          xs: '100%',
          sm: '100%',
          md: '1160px',
          lg: '1360px',
          xl: '1560px',
        },
      mx: 'auto',
      px: { xs: 0, sm: 0, md: 0 },
    }}>
      <Card
        className={isCompact ? 'hotel-form-compact-card' : undefined}
        sx={{
          ...getHotelSearchCardChrome({
            isEmbedded,
            isCompact,
            radius: L.formCardRadius,
          }),
          border: isEmbedded
            ? 'none'
            : (isCompact ? 'none' : '1px solid var(--yovo-grey-300, #D6D9E0)'),
          boxShadow: isEmbedded ? 'none' : (isCompact ? 'none' : 'var(--yovo-shadow-card, 0 0 23px 0 rgba(157, 157, 157, 0.25))'),
          overflow: 'visible',
          width: { xs: '100%', md: 'auto' },
          boxSizing: 'border-box',
          position: 'relative',
          zIndex: isEmbedded ? 1 : { xs: 2, md: 'auto' },
        }}
      >
        <CardContent sx={{
          pt: isEmbedded ? 0 : (isCompact ? undefined : { xs: L.formCardPaddingY, md: '40px' }),
          pb: isEmbedded ? 0 : (isCompact ? undefined : { xs: L.formCardPaddingY, md: '40px' }),
          pl: isEmbedded ? 0 : (isCompact ? undefined : { xs: L.formCardPaddingX, md: '32px', lg: '48px' }),
          pr: isEmbedded ? 0 : (isCompact ? undefined : { xs: L.formCardPaddingX, md: '32px', lg: '48px' }),
          display: 'flex',
          flexDirection: 'column',
          gap: isEmbedded ? 0 : (isCompact ? undefined : { xs: L.formCardSectionGap, md: 0 }),
          '&:last-child': {
            pb: isEmbedded ? 0 : (isCompact ? undefined : { xs: L.formCardPaddingY, md: '40px' }),
          },
        }}>
          {isCompact ? (
            <Box className={isEmbedded ? 'hotel-seo-search-panel hotel-form-compact-stack' : 'hotel-form-compact-stack'}>
            <>
              <Box className="hotel-form-compact-inner">
                {/* ── Mobile hotel card — Figma 3013:152666 (Yovotrip) ── */}
                <Box
                  className="hotel-form-compact-field"
                  data-hotel-destination-trigger
                  ref={locationInputRef}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    armDestinationCloseGuard();
                  }}
                >
                  <Typography component="span" className="hotel-form-compact-label hotel-form-compact-label--destination">
                    Destination
                  </Typography>
                  <TextField
                    className="hotel-form-compact-textfield"
                    fullWidth
                    placeholder="Enter City, Town etc"
                    value={locationInput}
                    size="small"
                    autoComplete="off"
                    inputProps={{
                      readOnly: true,
                      style: { cursor: 'pointer', caretColor: 'transparent' },
                    }}
                    onClick={() => {
                      armDestinationCloseGuard();
                      setValidationError('');
                      setMobileHotelDestOpen(true);
                      setLocationDropdownOpen(true);
                      if (!isEmbedded) {
                        promptBrowserLocationPermission();
                      }
                      handleLocationSearch(locationInput || '');
                    }}
                    onFocus={(e) => {
                      armDestinationCloseGuard();
                      setValidationError('');
                      setMobileHotelDestOpen(true);
                      setLocationDropdownOpen(true);
                      if (!locationInput || locationInput.length === 0) {
                        handleLocationSearch('');
                      }
                      e.target.blur();
                    }}
                  />
                  {validationError && !mobileHotelDestOpen && (
                    <Typography component="span" className="hotel-form-compact-error">
                      {validationError}
                    </Typography>
                  )}
                </Box>

                <Box className="hotel-form-compact-divider" aria-hidden="true" />

                {/* Check In · swap · Check Out — Figma 3013 date row; swap col uses hotel wrapper (stretch layout) */}
                <Box className="hotel-form-compact-date-row" data-date-picker>
                  <Box className="hotel-form-compact-field" sx={{ flex: '1 1 0', minWidth: 0 }}>
                    <Typography component="span" className="hotel-form-compact-label">
                      Check In
                    </Typography>
                    <Box
                      role="button"
                      tabIndex={0}
                      className="hotel-form-compact-value-trigger"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setMobileHotelDraftStart(checkInDate);
                          setMobileHotelDraftEnd(checkOutDate);
                          setHotelMobileDateSegment('checkin');
                          setDatePickerTrigger('checkin');
                          setDateRange([checkInDate, checkOutDate]);
                          setShowDatePicker(true);
                        }
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        setMobileHotelDraftStart(checkInDate);
                        setMobileHotelDraftEnd(checkOutDate);
                        setHotelMobileDateSegment('checkin');
                        setDatePickerTrigger('checkin');
                        setDateRange([checkInDate, checkOutDate]);
                        setShowDatePicker(true);
                      }}
                    >
                      <Typography
                        component="span"
                        className={`hotel-form-compact-value${checkInDate ? '' : ' hotel-form-compact-value--placeholder'}`}
                      >
                        {checkInDate ? formatDateMobileFigma(checkInDate) : 'Add date'}
                      </Typography>
                    </Box>
                  </Box>

                  <Box className="hotel-form-compact-swap-col">
                    <Box className="hotel-form-compact-swap-divider-track" aria-hidden="true">
                      <span className="hotel-form-compact-swap-divider-segment hotel-form-compact-swap-divider-segment--top" />
                      <span className="hotel-form-compact-swap-divider-segment hotel-form-compact-swap-divider-segment--bottom" />
                    </Box>
                    <button
                      type="button"
                      className="flight-form-swap"
                      onClick={handleSwapDates}
                      disabled={!checkInDate || !checkOutDate}
                      aria-label="Swap check-in and check-out dates"
                    >
                      <img src={hotel_swap_icon} alt="" className="flight-form-swap-icon" />
                    </button>
                  </Box>

                  <Box className="hotel-form-compact-field hotel-form-compact-field--align-end" sx={{ flex: '1 1 0', minWidth: 0 }}>
                    <Typography component="span" className="hotel-form-compact-label">
                      Check Out
                    </Typography>
                    <Box
                      role="button"
                      tabIndex={0}
                      className="hotel-form-compact-value-trigger"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setMobileHotelDraftStart(checkInDate);
                          setMobileHotelDraftEnd(checkOutDate);
                          setHotelMobileDateSegment('checkout');
                          setDatePickerTrigger('checkout');
                          if (checkInDate) {
                            setDateRange([checkInDate, checkOutDate]);
                            setFirstSelectedDate(null);
                          } else {
                            setDateRange([null, checkOutDate || null]);
                            setFirstSelectedDate(null);
                          }
                          setShowDatePicker(true);
                        }
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        setMobileHotelDraftStart(checkInDate);
                        setMobileHotelDraftEnd(checkOutDate);
                        setHotelMobileDateSegment('checkout');
                        setDatePickerTrigger('checkout');
                        if (checkInDate) {
                          setDateRange([checkInDate, checkOutDate]);
                          setFirstSelectedDate(null);
                        } else {
                          setDateRange([null, checkOutDate || null]);
                          setFirstSelectedDate(null);
                        }
                        setShowDatePicker(true);
                      }}
                    >
                      <Typography
                        component="span"
                        className={`hotel-form-compact-value${checkOutDate ? '' : ' hotel-form-compact-value--placeholder'}`}
                      >
                        {checkOutDate ? formatDateMobileFigma(checkOutDate) : 'Add date'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box className="hotel-form-compact-divider" aria-hidden="true" />

                {/* Guests + Rooms — separate rows per Figma; shared traveller sheet */}
                <Box sx={{ position: 'relative', width: '100%' }} data-guest-dropdown>
                  <Box
                    role="button"
                    tabIndex={0}
                    className="hotel-form-compact-field hotel-form-compact-value-trigger"
                    onClick={() => setShowGuestDropdown(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setShowGuestDropdown(true);
                      }
                    }}
                  >
                    <Typography component="span" className="hotel-form-compact-label">
                      Guests
                    </Typography>
                    <Typography component="span" className="hotel-form-compact-value">
                      {`${totalAdults} Adult${totalAdults === 1 ? '' : 's'}, ${totalChildren} ${totalChildren === 1 ? 'Child' : 'Children'}`}
                    </Typography>
                  </Box>

                  <Box className="hotel-form-compact-divider hotel-form-compact-divider--inset" aria-hidden="true" />

                  <Box
                    role="button"
                    tabIndex={0}
                    className="hotel-form-compact-field hotel-form-compact-value-trigger"
                    onClick={() => setShowGuestDropdown(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setShowGuestDropdown(true);
                      }
                    }}
                  >
                    <Typography component="span" className="hotel-form-compact-label">
                      Rooms
                    </Typography>
                    <Typography component="span" className="hotel-form-compact-value">
                      {`${rooms.length} Room${rooms.length === 1 ? '' : 's'}`}
                    </Typography>
                  </Box>

                  {renderGuestDropdown()}
                </Box>

                <Box className="hotel-form-compact-divider" aria-hidden="true" />

                {/* Nationality — same behavior; label matches Guests/Rooms */}
                <Box data-nationality-dropdown className="hotel-form-compact-field">
                  <Typography component="span" className="hotel-form-compact-label">
                    Nationality
                  </Typography>
                  <Box ref={nationalityAnchorRef} sx={{ position: 'relative', width: '100%' }}>
                    <TextField
                      className="hotel-form-compact-textfield hotel-form-compact-textfield--nationality"
                      fullWidth
                      value={nationalityLabel}
                      onClick={() => setShowNationalityDropdown(!showNationalityDropdown)}
                      size="small"
                      autoComplete="off"
                      inputProps={{ readOnly: true }}
                    />
                    <KeyboardArrowDownIcon sx={{
                      position: 'absolute',
                      right: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      fontSize: '20px',
                      color: '#7E808F',
                      pointerEvents: 'none',
                    }} />
                    {showNationalityDropdown && nationalityMenuPos && (
                      <Portal>
                        <Box
                          data-nationality-dropdown
                          className="hotel-form-compact-nationality-menu"
                          sx={{
                            position: 'fixed',
                            top: `${nationalityMenuPos.top}px`,
                            left: `${nationalityMenuPos.left}px`,
                            width: `${nationalityMenuPos.width}px`,
                            borderRadius: '12px',
                            boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                            border: '1px solid #e0e0e0',
                            minWidth: '200px',
                            maxHeight: '280px',
                            overflowY: 'auto',
                            py: '6px',
                            scrollbarWidth: 'thin',
                            scrollbarColor: '#ccc transparent',
                          }}
                        >
                          {NATIONALITY_OPTIONS.map((n) => (
                            <Box
                              key={n.code}
                              onClick={() => { setGuestNationality(n.code); setShowNationalityDropdown(false); }}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: '16px',
                                py: '10px',
                                cursor: 'pointer',
                                backgroundColor: guestNationality === n.code ? '#F5F5F7' : 'transparent',
                                '&:hover': { backgroundColor: '#F5F5F7' },
                                transition: 'background-color 0.1s',
                              }}
                            >
                              <Typography sx={{
                                fontFamily: 'Host Grotesk',
                                fontSize: '14px',
                                fontWeight: guestNationality === n.code ? 700 : 500,
                                color: '#20202A',
                              }}>
                                {n.label}
                              </Typography>
                              <Typography sx={{
                                fontFamily: 'Host Grotesk',
                                fontSize: '12px',
                                fontWeight: 500,
                                color: '#7E808F',
                                ml: '12px',
                              }}>
                                {n.code}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Portal>
                    )}
                  </Box>
                  {guestNationality !== 'IN' && location?.countryCode === 'IN' && (
                    <Typography component="span" className="hotel-form-compact-helper">
                      Passport will be required during booking
                    </Typography>
                  )}
                </Box>
              </Box>

              <Button
                fullWidth
                onClick={handleSearch}
                disabled={isResolving}
                sx={{
                  bgcolor: '#20202A',
                  color: '#FBFBFB',
                  borderRadius: '12px',
                  height: '48px',
                  minHeight: '48px',
                  px: '24px',
                  py: '10px',
                  fontFamily: 'Host Grotesk',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '20px',
                  letterSpacing: '0.4px',
                  textTransform: 'none',
                  touchAction: 'manipulation',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#383A47', boxShadow: 'none' },
                  '&:active': { transform: 'scale(0.99)' },
                }}
              >
                Let's Go
              </Button>
            </>
            </Box>
          ) : (
          (() => {
            // Desktop typography tokens — Figma 2586:118700.
            // Label: Host Grotesk Medium 500, 12px / 16px line / 0.25px tracking, color #20202A.
            // Value: Host Grotesk Bold 700, 20px / 28px line, #20202A when filled, #7E808F as placeholder.
            const fieldLabelSx = {
              fontFamily: 'Host Grotesk',
              fontSize: '12px',
              fontWeight: 500,
              lineHeight: '16px',
              letterSpacing: '0.25px',
              color: '#20202A',
              textAlign: 'left',
              whiteSpace: 'nowrap',
            };
            const fieldValueBaseSx = {
              fontFamily: 'Host Grotesk',
              fontSize: '20px',
              fontWeight: 700,
              lineHeight: '28px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              width: '100%',
            };
            const fieldStackSx = {
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              justifyContent: 'center',
              minWidth: 0,
            };
            // Combined display: "X Adult(s), Y Child(ren)" only if there are children
            const guestsDisplay = `${totalAdults} ${totalAdults === 1 ? 'Adult' : 'Adults'}` +
              (totalChildren > 0 ? `, ${totalChildren} ${totalChildren === 1 ? 'Child' : 'Children'}` : '');

            return (
              <Box className={isEmbedded ? 'hotel-seo-search-panel' : undefined}>
              <Box
                className="hotel-search-strip"
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: { xs: 'stretch', md: 'center' },
                  mb: 0,
                  width: '100%',
                  flexWrap: { xs: 'wrap', md: 'wrap', lg: 'nowrap' },
                  flexDirection: { xs: 'row', md: 'column', lg: 'row' },
                  justifyContent: 'flex-start',
                  gap: { xs: 1.5, md: HOTEL_SEARCH_STRIP_ROW_GAP, lg: 0 },
                  background: isEmbedded ? 'transparent' : 'var(--yovo-base-white, #FBFBFB)',
                  border: isEmbedded ? 'none' : '1px solid var(--yovo-grey-300, #D6D9E0)',
                  borderRadius: isEmbedded ? 0 : '24px',
                  position: isEmbedded ? undefined : { md: 'relative' },
                  padding: getHotelSearchStripPadding(isEmbedded),
                }}
              >
                <Box sx={getHotelSearchStripBandLayout('primary', !isEmbedded)}>
                {/* ── DESTINATION ── */}
                <Box
                  className="hotel-search-field"
                  ref={locationInputRef}
                  sx={{
                    ...fieldStackSx,
                    ...getHotelSearchDestinationLayout(),
                    py: { xs: '10px', md: 0 },
                    pl: { xs: '4px', md: 0 },
                    pr: { xs: '4px', md: 0 },
                    borderBottom: { xs: '1px solid #E8E9ED', md: 'none' },
                  }}
                >
                  <Typography sx={fieldLabelSx}>Destination</Typography>
                  <Box ref={destinationAnchorRef} sx={{ position: 'relative', width: '100%' }}>
                    <TextField
                      fullWidth
                      placeholder="Enter City, Town etc"
                      value={locationInput}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        armDestinationCloseGuard();
                        setValidationError('');
                        openLocationDropdown();
                        if (!locationInput || locationInput.length === 0) {
                          handleLocationSearch('');
                        }
                      }}
                      size="small"
                      autoComplete="off"
                      inputProps={{
                        readOnly: true,
                        style: { cursor: 'pointer', caretColor: 'transparent' },
                      }}
                      onFocus={(e) => e.target.blur()}
                      sx={{
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 0,
                          minHeight: 'auto',
                          height: 'auto',
                          background: 'transparent',
                          border: 'none',
                          padding: 0,
                          cursor: 'pointer',
                          '& fieldset': { border: 'none' },
                          '&.Mui-focused fieldset': { border: 'none', boxShadow: 'none' },
                        },
                        '& .MuiInputBase-input': {
                          ...fieldValueBaseSx,
                          color: locationInput ? '#20202A' : '#7E808F',
                          padding: '0 !important',
                          margin: 0,
                          cursor: 'pointer',
                        },
                        '& .MuiInputBase-input::placeholder': {
                          color: '#7E808F',
                          opacity: 1,
                        },
                      }}
                    />
                    {validationError && (
                      <Typography sx={{
                        color: '#d32f2f',
                        fontSize: '12px',
                        fontFamily: 'Host Grotesk',
                        mt: 0.5,
                      }}>
                        {validationError}
                      </Typography>
                    )}

                    {/* Desktop dropdown — Flight-style: search bar inside the panel, results below. */}
                    {locationDropdownOpen && (!useEmbeddedPortals || embeddedLocationPos) && (() => {
                      const locationDropdown = (
                      <Box
                        data-location-dropdown
                        onMouseDown={(e) => e.stopPropagation()}
                        className={`hotel-location-dropdown hotel-location-dropdown--desktop${useEmbeddedPortals && embeddedLocationPos ? ' hotel-dropdown-portaled' : ''}`}
                        style={useEmbeddedPortals && embeddedLocationPos ? {
                          position: 'fixed',
                          top: `${embeddedLocationPos.top}px`,
                          left: `${embeddedLocationPos.left}px`,
                          zIndex: 1300,
                        } : undefined}
                      >
                        <Box className="hotel-dropdown-search-bar">
                          <img
                            src={searchNormalIcon}
                            alt=""
                            className="hotel-dropdown-search-bar-icon"
                            style={{ width: '20px', height: '20px' }}
                          />
                          <input
                            ref={desktopDropdownSearchRef}
                            type="text"
                            placeholder="Search city, hotel, area or landmark"
                            className="hotel-dropdown-search-input"
                            value={locationInput}
                            onChange={(e) => handleLocationInputChange(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleSearch();
                              }
                            }}
                            autoComplete="off"
                            aria-label="Search city or hotel"
                          />
                        </Box>

                        <HotelNearbyLocationOption
                          variant="desktop"
                          gtmContext="home"
                          onSelect={(loc) => handleLocationSelect(toHotelLocationSelectInput(loc))}
                          onError={setValidationError}
                        />

                        <Box className="hotel-dropdown-results">
                          {locationLoading && cityResults.length === 0 && hotelResults.length === 0 && locationResults.length === 0 ? (
                            <Box className="hotel-dropdown-empty">Searching…</Box>
                          ) : cityResults.length === 0 && hotelResults.length === 0 && locationResults.length === 0 ? (
                            <Box className="hotel-dropdown-empty">No matches found</Box>
                          ) : (
                            (() => {
                              const citySection = sliceAutocompleteSection(cityResults, expandedAutocomplete.city);
                              const hotelSection = sliceAutocompleteSection(hotelResults, expandedAutocomplete.hotel);
                              const locationSection = sliceAutocompleteSection(locationResults, expandedAutocomplete.location);
                              const expandSection = (section) => {
                                setExpandedAutocomplete((prev) => ({ ...prev, [section]: true }));
                              };
                              const renderCities = () => cityResults.length > 0 && (
                                <>
                                  <Typography
                                    className="hotel-dropdown-section-title"
                                    sx={{
                                      borderTop: queryType === 'hotel' && hotelResults.length > 0 ? '1px solid #E8E9ED' : 'none',
                                    }}
                                  >
                                    Cities & Destinations
                                  </Typography>
                                  {citySection.visible.map((loc, index) => (
                                    <Box
                                      key={`city-${loc.cityId || index}`}
                                      onClick={() => handleLocationSelect({ ...loc, type: 'city', id: loc.cityId, city: loc.cityName, country: loc.countryName })}
                                      className="hotel-location-option"
                                      {...createGtmAttributes(GTM_CLICK_IDS.HOTEL_SEARCH_AUTOCOMPLETE_SELECT, {
                                        [GTM_CONTEXT_ATTRS.CONTEXT]: 'city',
                                      })}
                                    >
                                      <Box sx={{
                                        width: '18px', height: '18px', marginRight: '12px',
                                        flexShrink: 0, marginTop: '2px',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      }}>
                                        <LocationCityIcon sx={{ fontSize: '18px', color: HOTEL_SEARCH_ICON_COLOR.CITY }} />
                                      </Box>
                                      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                                          <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{
                                              fontWeight: 600,
                                              color: '#20202A',
                                              fontSize: '14px',
                                              marginBottom: '4px',
                                              whiteSpace: 'nowrap',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                            }}>
                                              <HotelAutocompleteLabel label={loc.displayName} />
                                            </Typography>
                                            {/*<Typography sx={{*/}
                                            {/*  fontSize: '12px',*/}
                                            {/*  color: '#7E808F',*/}
                                            {/*  whiteSpace: 'nowrap',*/}
                                            {/*  overflow: 'hidden',*/}
                                            {/*  textOverflow: 'ellipsis',*/}
                                            {/*}}>*/}
                                            {/*  {loc.countryName}{loc.hotelCount ? ` \u2022 ${loc.hotelCount.toLocaleString()} hotels` : ''}*/}
                                            {/*</Typography>*/}
                                          </Box>
                                          {loc.countryCode && (
                                            <Typography sx={{
                                              fontWeight: 500,
                                              color: '#20202A',
                                              fontSize: '14px',
                                              flexShrink: 0,
                                              marginLeft: '12px',
                                              alignSelf: 'flex-start',
                                            }}>
                                              {loc.countryCode}
                                            </Typography>
                                          )}
                                        </Box>
                                      </Box>
                                    </Box>
                                  ))}
                                  <HotelAutocompleteLoadMore
                                    visible={citySection.showLoadMore}
                                    onLoadMore={() => expandSection('city')}
                                  />
                                </>
                              );

                              const renderHotels = () => hotelResults.length > 0 && (
                                <>
                                  <Typography
                                    className="hotel-dropdown-section-title"
                                    sx={{
                                      borderTop: queryType === 'city' && cityResults.length > 0 ? '1px solid #E8E9ED' : 'none',
                                    }}
                                  >
                                    Hotels
                                  </Typography>
                                  {hotelSection.visible.map((loc, index) => (
                                    <Box
                                      key={`hotel-${loc.hotelCode || index}`}
                                      onClick={() => handleLocationSelect({ ...loc, type: 'hotel', id: loc.hotelCode, name: loc.hotelName, city: loc.cityName, country: loc.countryName })}
                                      className="hotel-location-option"
                                      {...createGtmAttributes(GTM_CLICK_IDS.HOTEL_SEARCH_AUTOCOMPLETE_SELECT, {
                                        [GTM_CONTEXT_ATTRS.CONTEXT]: 'hotel',
                                      })}
                                    >
                                      <Box sx={{
                                        width: '18px', height: '18px', marginRight: '12px',
                                        flexShrink: 0, marginTop: '2px',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      }}>
                                        <HotelIcon sx={{ fontSize: '16px', color: HOTEL_SEARCH_ICON_COLOR.HOTEL }} />
                                      </Box>
                                      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                        <Typography component="div" sx={{
                                          fontWeight: 600, color: '#20202A', fontSize: '14px',
                                          minWidth: 0,
                                        }}>
                                          <HotelFuzzyMatchName name={loc.hotelName} />
                                        </Typography>
                                        <Typography sx={{
                                          fontSize: '12px', color: '#7E808F',
                                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                        }}>
                                          <HotelAutocompleteLabel label={loc.label} />
                                          <HotelAutocompleteStars value={loc.starRating} />
                                        </Typography>
                                      </Box>
                                    </Box>
                                  ))}
                                  <HotelAutocompleteLoadMore
                                    visible={hotelSection.showLoadMore}
                                    onLoadMore={() => expandSection('hotel')}
                                  />
                                </>
                              );

                              const renderLocations = () => locationResults.length > 0 && (
                                <>
                                  <Typography
                                    className="hotel-dropdown-section-title"
                                    sx={{
                                      borderTop: (cityResults.length > 0 || hotelResults.length > 0) ? '1px solid #E8E9ED' : 'none',
                                    }}
                                  >
                                    Locations
                                  </Typography>
                                  {locationSection.visible.map((loc, index) => (
                                    <Box
                                      key={`loc-${loc.locationId || index}`}
                                      onClick={() => handleLocationSelect({
                                        ...loc,
                                        type: 'location',
                                        id: loc.locationId,
                                        name: loc.displayName,
                                        city: loc.cityName,
                                        country: loc.countryName,
                                      })}
                                      className="hotel-location-option"
                                      {...createGtmAttributes(GTM_CLICK_IDS.HOTEL_SEARCH_AUTOCOMPLETE_SELECT, {
                                        [GTM_CONTEXT_ATTRS.CONTEXT]: 'location',
                                      })}
                                    >
                                      <Box sx={{
                                        width: '18px', height: '18px', marginRight: '12px',
                                        flexShrink: 0, marginTop: '2px',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      }}>
                                        <PlaceIcon sx={{ fontSize: '18px', color: HOTEL_SEARCH_ICON_COLOR.LOCATION }} />
                                      </Box>
                                      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                          <Typography sx={{
                                            fontWeight: 600, color: '#20202A', fontSize: '14px',
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0,
                                          }}>
                                            <HotelAutocompleteLabel label={loc.displayName} />
                                          </Typography>
                                          <HotelLocationTypeIcon locationType={loc.locationType} />
                                        </Box>
                                        <Typography sx={{
                                          fontSize: '12px', color: '#7E808F',
                                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                        }}>
                                          <HotelAutocompleteLabel label={loc.label} />
                                          {/*{loc.cityName}{loc.countryName ? `, ${loc.countryName}` : ''}*/}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  ))}
                                  <HotelAutocompleteLoadMore
                                    visible={locationSection.showLoadMore}
                                    onLoadMore={() => expandSection('location')}
                                  />
                                </>
                              );

                              const sectionRenderers = {
                                city: renderCities,
                                hotel: renderHotels,
                                location: renderLocations,
                              };

                              return getHotelAutocompleteSectionOrder(queryType).map((section) => (
                                <Fragment key={section}>{sectionRenderers[section]?.()}</Fragment>
                              ));
                            })()
                          )}
                        </Box>
                      </Box>
                      );
                      if (useEmbeddedPortals && embeddedLocationPos) {
                        return <Portal>{locationDropdown}</Portal>;
                      }
                      return locationDropdown;
                    })()}
                  </Box>
                </Box>

                {/* ── CHECK-IN ── */}
                <Box
                  className="hotel-search-field"
                  sx={{
                    ...fieldStackSx,
                    ...getHotelSearchDateColumnLayout(),
                    width: { xs: '100%', md: 'auto' },
                    py: { xs: '10px', md: 0 },
                    pl: { xs: '4px', md: 0 },
                    pr: { xs: '4px', md: 0 },
                    borderBottom: { xs: '1px solid #E8E9ED', md: 'none' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography sx={fieldLabelSx}>Check In</Typography>
                    <KeyboardArrowDownIcon sx={{ fontSize: '16px', color: '#545665', ml: '4px' }} />
                  </Box>
                  <Box ref={checkInDateAnchorRef} sx={{ position: 'relative', width: '100%' }} data-date-picker>
                    <Box
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        setDatePickerTrigger('checkin');
                        setDateRange([checkInDate, checkOutDate]);
                        setShowDatePicker(true);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setDatePickerTrigger('checkin');
                          setDateRange([checkInDate, checkOutDate]);
                          setShowDatePicker(true);
                        }
                      }}
                      sx={{
                        width: '100%',
                        cursor: 'pointer',
                        WebkitTapHighlightColor: 'transparent',
                      }}
                    >
                      <Typography sx={{
                        ...getHotelSearchDateValueSx(),
                        color: checkInDate ? '#20202A' : '#7E808F',
                      }}>
                        {checkInDate ? formatDate(checkInDate) : 'Add Dates'}
                      </Typography>
                    </Box>
                    {renderDatePicker()}
                  </Box>
                </Box>

                {/* ── CHECK-OUT ── */}
                <Box
                  className="hotel-search-field"
                  sx={{
                    ...fieldStackSx,
                    ...getHotelSearchDateColumnLayout(),
                    width: { xs: '100%', md: 'auto' },
                    py: { xs: '10px', md: 0 },
                    pl: { xs: '4px', md: 0 },
                    pr: { xs: '4px', md: 0 },
                    borderBottom: { xs: '1px solid #E8E9ED', md: 'none' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography sx={fieldLabelSx}>Check Out</Typography>
                    <KeyboardArrowDownIcon sx={{ fontSize: '16px', color: '#545665', ml: '4px' }} />
                  </Box>
                  <Box ref={checkOutDateAnchorRef} sx={{ position: 'relative', width: '100%' }} data-date-picker>
                    <Box
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        setDatePickerTrigger('checkout');
                        if (checkInDate) {
                          setDateRange([checkInDate, checkOutDate]);
                          setFirstSelectedDate(null);
                        } else {
                          setDateRange([null, checkOutDate || null]);
                          setFirstSelectedDate(null);
                        }
                        setShowDatePicker(true);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setDatePickerTrigger('checkout');
                          if (checkInDate) {
                            setDateRange([checkInDate, checkOutDate]);
                            setFirstSelectedDate(null);
                          } else {
                            setDateRange([null, checkOutDate || null]);
                            setFirstSelectedDate(null);
                          }
                          setShowDatePicker(true);
                        }
                      }}
                      sx={{
                        width: '100%',
                        cursor: 'pointer',
                        WebkitTapHighlightColor: 'transparent',
                      }}
                    >
                      <Typography sx={{
                        ...getHotelSearchDateValueSx(),
                        color: checkOutDate ? '#20202A' : '#7E808F',
                      }}>
                        {checkOutDate ? formatDate(checkOutDate) : 'Add Dates'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                </Box>

                <Box sx={getHotelSearchStripBandLayout('secondary', !isEmbedded)}>
                {/* ── GUESTS ── (separate field per Figma; opens shared Rooms & Guests picker) */}
                <Box
                  className="hotel-search-field"
                  data-guest-dropdown
                  ref={guestAnchorRef}
                  sx={{
                    ...fieldStackSx,
                    ...getHotelSearchPackedFieldLayout('1 1 0', '120px'),
                    py: { xs: '10px', md: 0 },
                    pl: { xs: '4px', md: 0 },
                    pr: { xs: '4px', md: 0 },
                    borderBottom: { xs: '1px solid #E8E9ED', md: 'none' },
                    position: 'relative',
                  }}
                >
                  <Typography sx={fieldLabelSx}>Guests</Typography>
                  {/* Always open (don't toggle) — Rooms also opens this same picker, so toggling
                      between fields would close it unexpectedly. */}
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => setShowGuestDropdown(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setShowGuestDropdown(true);
                      }
                    }}
                    sx={{
                      width: { xs: '100%', md: 'auto' },
                      cursor: 'pointer',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                  >
                    <Typography sx={{ ...fieldValueBaseSx, color: '#20202A', width: { xs: '100%', md: 'auto' } }}>
                      {guestsDisplay}
                    </Typography>
                  </Box>
                  {renderGuestDropdown()}
                </Box>

                {/* ── ROOMS ── (separate field per Figma; opens same picker) */}
                <Box
                  className="hotel-search-field"
                  data-guest-dropdown
                  sx={{
                    ...fieldStackSx,
                    ...getHotelSearchPackedFieldLayout('0.8 1 0', '100px'),
                    py: { xs: '10px', md: 0 },
                    pl: { xs: '4px', md: 0 },
                    pr: { xs: '4px', md: 0 },
                    borderBottom: { xs: '1px solid #E8E9ED', md: 'none' },
                  }}
                >
                  <Typography sx={fieldLabelSx}>Rooms</Typography>
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => setShowGuestDropdown(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setShowGuestDropdown(true);
                      }
                    }}
                    sx={{
                      width: { xs: '100%', md: 'auto' },
                      cursor: 'pointer',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                  >
                    <Typography sx={{ ...fieldValueBaseSx, color: '#20202A', width: { xs: '100%', md: 'auto' } }}>
                      {`${rooms.length} ${rooms.length === 1 ? 'Room' : 'Rooms'}`}
                    </Typography>
                  </Box>
                </Box>

                {/* ── NATIONALITY + LET'S GO stay together and pin to the end when they wrap ── */}
                <Box sx={getHotelSearchWrappedTailLayout(!isEmbedded)}>
                {/* ── NATIONALITY ── (kept for booking logic; styled to match field hierarchy) */}
                <Box
                  className="hotel-search-field"
                  data-nationality-dropdown
                  sx={{
                    ...fieldStackSx,
                    ...getHotelSearchPackedFieldLayout('1 1 0', '120px'),
                    py: { xs: '10px', md: 0 },
                    pl: { xs: '4px', md: 0 },
                    pr: { xs: '4px', md: 0 },
                    borderBottom: { xs: '1px solid #E8E9ED', md: 'none' },
                    position: 'relative',
                  }}
                >
                  <Typography sx={fieldLabelSx}>Nationality</Typography>
                  <Box ref={desktopNationalityAnchorRef} sx={{ position: 'relative', width: '100%' }}>
                    {/* Inline flex keeps the chevron flush against the value text regardless of
                        field width — avoids the stretched gap caused by absolute positioning. */}
                    <Box
                      role="button"
                      tabIndex={0}
                      onClick={() => setShowNationalityDropdown(!showNationalityDropdown)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setShowNationalityDropdown(!showNationalityDropdown);
                        }
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        width: { xs: '100%', md: 'auto' },
                        minWidth: 0,
                        cursor: 'pointer',
                        WebkitTapHighlightColor: 'transparent',
                      }}
                    >
                      <Typography
                        sx={{
                          ...fieldValueBaseSx,
                          color: '#20202A',
                          width: 'auto',
                          minWidth: 0,
                          flexShrink: 1,
                        }}
                      >
                        {nationalityLabel}
                      </Typography>
                      <KeyboardArrowDownIcon
                        sx={{
                          fontSize: '20px',
                          color: '#7E808F',
                          flexShrink: 0,
                        }}
                      />
                      {/* Passport-required hint shown inline as an info-icon tooltip — keeps the
                          field height fixed at the single-line value height so the strip stays
                          visually aligned with the other fields. */}
                      {guestNationality !== 'IN' && location?.countryCode === 'IN' && (
                        <Tooltip
                          title="Passport will be required during booking"
                          arrow
                          placement="top"
                          enterTouchDelay={0}
                          leaveTouchDelay={4000}
                        >
                          <InfoOutlinedIcon
                            role="img"
                            aria-label="Passport will be required during booking"
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              fontSize: '16px',
                              color: '#e65100',
                              cursor: 'help',
                              flexShrink: 0,
                              ml: '2px',
                            }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                    {showNationalityDropdown && useEmbeddedPortals && nationalityMenuPos && (
                      <Portal>
                        <Box
                          data-nationality-dropdown
                          className="hotel-dropdown-portaled"
                          style={{
                            position: 'fixed',
                            top: `${nationalityMenuPos.top}px`,
                            left: `${nationalityMenuPos.left}px`,
                            width: `${Math.max(nationalityMenuPos.width, 220)}px`,
                            zIndex: 1300,
                          }}
                          sx={{
                            bgcolor: '#fff',
                            borderRadius: '12px',
                            boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                            border: '1px solid #e0e0e0',
                            minWidth: '220px',
                            maxHeight: '280px',
                            overflowY: 'auto',
                            zIndex: 1300,
                            py: '6px',
                            scrollbarWidth: 'thin',
                            scrollbarColor: '#ccc transparent',
                          }}
                        >
                          {NATIONALITY_OPTIONS.map((n) => (
                            <Box
                              key={n.code}
                              onClick={() => { setGuestNationality(n.code); setShowNationalityDropdown(false); }}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: '16px',
                                py: '10px',
                                cursor: 'pointer',
                                backgroundColor: guestNationality === n.code ? '#F5F5F7' : 'transparent',
                                '&:hover': { backgroundColor: '#F5F5F7' },
                                transition: 'background-color 0.1s',
                              }}
                            >
                              <Typography sx={{
                                fontFamily: 'Host Grotesk',
                                fontSize: '14px',
                                fontWeight: guestNationality === n.code ? 700 : 500,
                                color: '#20202A',
                              }}>
                                {n.label}
                              </Typography>
                              <Typography sx={{
                                fontFamily: 'Host Grotesk',
                                fontSize: '12px',
                                fontWeight: 500,
                                color: '#7E808F',
                                ml: '12px',
                              }}>
                                {n.code}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Portal>
                    )}
                    {showNationalityDropdown && !useEmbeddedPortals && (
                      <Box
                        data-nationality-dropdown
                        sx={{
                          position: 'absolute',
                          top: 'calc(100% + 8px)',
                          left: { xs: 0, md: 'auto' },
                          right: { xs: 0, md: 0 },
                          bgcolor: '#fff',
                          borderRadius: '12px',
                          boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                          border: '1px solid #e0e0e0',
                          minWidth: '220px',
                          maxHeight: '280px',
                          overflowY: 'auto',
                          zIndex: 200,
                          py: '6px',
                          scrollbarWidth: 'thin',
                          scrollbarColor: '#ccc transparent',
                        }}
                      >
                        {NATIONALITY_OPTIONS.map((n) => (
                          <Box
                            key={n.code}
                            onClick={() => { setGuestNationality(n.code); setShowNationalityDropdown(false); }}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              px: '16px',
                              py: '10px',
                              cursor: 'pointer',
                              backgroundColor: guestNationality === n.code ? '#F5F5F7' : 'transparent',
                              '&:hover': { backgroundColor: '#F5F5F7' },
                              transition: 'background-color 0.1s',
                            }}
                          >
                            <Typography sx={{
                              fontFamily: 'Host Grotesk',
                              fontSize: '14px',
                              fontWeight: guestNationality === n.code ? 700 : 500,
                              color: '#20202A',
                            }}>
                              {n.label}
                            </Typography>
                            <Typography sx={{
                              fontFamily: 'Host Grotesk',
                              fontSize: '12px',
                              fontWeight: 500,
                              color: '#7E808F',
                              ml: '12px',
                            }}>
                              {n.code}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                  {/* Helper text moved to the inline info-icon tooltip above so the field
                      stays single-line and the strip remains aligned with the other fields. */}
                </Box>

                {/* ── LET'S GO Button ── per Figma 2586:118722 */}
                <Box
                  className="hotel-search-btn"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    marginLeft: { xs: 0, lg: '8px' },
                    width: { xs: '100%', md: 'auto' },
                    py: { xs: '8px', md: 0 },
                    flexShrink: 0,
                  }}
                >
                  <Button
                    onClick={handleSearch}
                    disabled={isResolving}
                    sx={{
                      background: '#20202A',
                      color: '#FBFBFB',
                      borderRadius: { xs: '12px', md: '16px' },
                      display: 'flex',
                      width: { xs: '100%', md: 'auto' },
                      height: '48px',
                      minHeight: '48px',
                      padding: { xs: '12px 24px', md: '6px 16px' },
                      justifyContent: 'center',
                      alignItems: 'center',
                      letterSpacing: '0.4px',
                      fontFamily: 'Host Grotesk, -apple-system, Roboto, Helvetica, sans-serif',
                      fontWeight: 600,
                      fontSize: '14px',
                      lineHeight: '20px',
                      textTransform: 'none',
                      whiteSpace: 'nowrap',
                      boxShadow: 'none',
                      transition: 'background 0.2s, transform 0.15s',
                      '&:hover': { backgroundColor: '#383A47', boxShadow: 'none' },
                      '&:active': { transform: 'scale(0.97)' },
                    }}
                  >
                    Let's Go
                  </Button>
                </Box>
                </Box>
                </Box>
              </Box>
              </Box>
            );
          })()
          )}
        </CardContent>
      </Card>

      <HotelMobileDestinationSheet
        open={mobileHotelDestOpen}
        onClose={() => {
          setMobileHotelDestOpen(false);
          setLocationDropdownOpen(false);
        }}
        locationInput={locationInput}
        onLocationInputChange={handleLocationInputChange}
        onSearch={handleSearch}
        onOpenSearch={() => {
          setLocationDropdownOpen(true);
          setValidationError('');
          if (!isEmbedded) {
            promptBrowserLocationPermission();
          }
          if (!locationInput || locationInput.length === 0) {
            handleLocationSearch('');
          }
        }}
        locationLoading={locationLoading}
        cityResults={cityResults}
        hotelResults={hotelResults}
        queryType={queryType}
        onSelectCity={(loc) => handleLocationSelect({
          ...loc,
          type: 'city',
          id: loc.cityId,
          city: loc.cityName,
          country: loc.countryName,
        })}
        onSelectHotel={(loc) => handleLocationSelect({
          ...loc,
          type: 'hotel',
          id: loc.hotelCode,
          name: loc.hotelName,
          city: loc.cityName,
          country: loc.countryName,
        })}
        locationResults={locationResults}
        onSelectLocation={(loc) => handleLocationSelect({
          ...loc,
          type: 'location',
          id: loc.locationId,
          name: loc.displayName,
          city: loc.cityName,
          country: loc.countryName,
        })}
        validationError={validationError}
        onNearbyError={setValidationError}
      />

      <MobileSearchDateSheet
        open={isCompact && showDatePicker}
        onClose={() => {
          setShowDatePicker(false);
          setDatePickerTrigger(null);
        }}
        onSave={saveMobileHotelDates}
        tripType="roundtrip"
        variant="hotel"
        dateDraftStart={mobileHotelDraftStart}
        dateDraftEnd={mobileHotelDraftEnd}
        hotelDateSegmentFocused={hotelMobileDateSegment}
        onHotelDateSegmentFocus={setHotelMobileDateSegment}
        onClearCheckin={handleHotelClearCheckin}
        onClearCheckout={handleHotelClearCheckout}
      >
        {renderHotelMobileCalendars()}
      </MobileSearchDateSheet>

      {/* Nationality Block Modal — foreign guest + international destination */}
      {showNationalityBlock && (
        <Box sx={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '16px',
        }}
          onClick={() => setShowNationalityBlock(false)}
        >
          <Box sx={{
            background: '#fff', borderRadius: '16px', maxWidth: '440px', width: '100%',
            padding: { xs: '24px 20px', sm: '32px 28px' },
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '16px' }}>
              <Box sx={{
                width: '44px', height: '44px', borderRadius: '12px',
                backgroundColor: '#FFF3E0', display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Typography sx={{ fontSize: '22px' }}>&#9888;</Typography>
              </Box>
              <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#20202A', fontFamily: 'Host Grotesk' }}>
                Booking Not Available
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '14px', color: '#555', fontFamily: 'Host Grotesk', lineHeight: 1.6, mb: '24px' }}>
              International hotel bookings are available exclusively for <strong>Indian nationals</strong>. 
              You can search for hotels within India with your selected nationality, or switch to Indian nationality for international destinations.
            </Typography>
            <Box sx={{ display: 'flex', gap: '10px', flexDirection: { xs: 'column', sm: 'row' } }}>
              <Button
                fullWidth
                onClick={() => { setGuestNationality('IN'); setShowNationalityBlock(false); }}
                sx={{
                  background: '#20202A', color: '#fff', borderRadius: '10px', height: '44px',
                  fontFamily: 'Host Grotesk', fontWeight: 600, fontSize: '14px', textTransform: 'none',
                  '&:hover': { backgroundColor: '#2e2e2e' },
                }}
              >
                Switch to Indian
              </Button>
              <Button
                fullWidth
                onClick={() => setShowNationalityBlock(false)}
                sx={{
                  background: '#fff', color: '#20202A', borderRadius: '10px', height: '44px',
                  border: '1.5px solid #E8E9ED',
                  fontFamily: 'Host Grotesk', fontWeight: 600, fontSize: '14px', textTransform: 'none',
                  '&:hover': { backgroundColor: '#f5f5f5' },
                }}
              >
                Go Back
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default HotelForm;

